import { isProd } from '@/shared/utils'
import { Locales } from '@/utils/language'
import { getCookie } from '@/shared/utils/url-utils'

/**
 * SigninPaywall 조건 판단을 위한 Props 인터페이스
 */
export interface SigninPaywallProps {
  /** 사용자 로그인 상태 */
  isLoggedIn: boolean
  /** 현재 경로 */
  currentPath: string
  /** 현재 언어/로케일 */
  language: Locales
  /** 사용자 행동 데이터 */
  userBehavior?: {
    /** 스크롤 백분율 (0-100) */
    scrollPercent?: number
    /** 페이지 방문 시간 (초) */
    timeOnPage?: number
    /** 페이지 방문 횟수 */
    visitCount?: number
  }
}

/**
 * 사용자가 로그인했는지 확인
 */
export const isUserLoggedIn = (props: SigninPaywallProps): boolean => {
  return props.isLoggedIn
}

/**
 * 포함 경로인지 확인
 */
export const isIncludedPath = (props: SigninPaywallProps): boolean => {
  // 포함 경로 패턴을 정규식으로 정의
  const includedPathRegexes = [
    /^\/$/,
    /^\/event(s)?(\/|$)/,
    /^\/hospital(s)?(\/|$)/,
    /^\/column(s)?(\/|$)/,
    /^\/posts(s)?(\/|$)/,
    /^\/community(s)?(\/|$)/,
    /^\/medical-tips(s)?(\/|$)/,
    /^\/search(s)?(\/|$)/,
    /^\/reviews(s)?(\/|$)/,
  ]

  return includedPathRegexes.some((regex) => regex.test(props.currentPath))
}

/**
 * 한국 사용자인지 확인 (한국 사용자에 대해 block paywall 표시)
 */
export const isKoreanUser = (props: SigninPaywallProps): boolean => {
  return props.language === Locales.KO_KR
}

/**
 * 사용자가 최근에 paywall을 닫았는지 확인
 */
export const hasUserDismissedRecently = (props: SigninPaywallProps): boolean => {
  try {
    const dismissedAt = localStorage.getItem('signin_paywall_dismissed_at')
    if (!dismissedAt) return false

    const dismissedTime = parseInt(dismissedAt, 10)
    const now = Date.now()
    const oneHour = 60 * 60 * 1000 // 1시간

    return now - dismissedTime < oneHour
  } catch (error) {
    // localStorage 접근 실패 시 false 반환
    return false
  }
}

/**
 * 크롤러인지 확인 (크롤러는 제외)
 */
export const isCrawler = (): boolean => {
  if (typeof navigator === 'undefined') return true

  const userAgent = navigator.userAgent.toLowerCase()
  const crawlerPatterns = [
    'bot',
    'crawler',
    'spider',
    'crawling',
    'facebook',
    'twitter',
    'google',
    'yahoo',
    'bing',
    'msn',
    'slurp',
    'duckduckbot',
  ]

  return crawlerPatterns.some((pattern) => userAgent.includes(pattern))
}

/**
 * 현재 시간이 paywall 표시 시간 범위 내인지 확인
 * 한국 시간 기준: 9월 23일 오전 8시 ~ 9월 25일 오전 8시
 */
export const isWithinPaywallTimeRange = (): boolean => {
  try {
    if (!isProd) return true

    // 한국 시간으로 현재 날짜/시간 가져오기
    const koreaTime = new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Seoul',
    })
    const now = new Date(koreaTime)

    const year = now.getFullYear()
    const month = now.getMonth() + 1 // 0-based이므로 +1
    const date = now.getDate()
    const hour = now.getHours()

    // 9월 23일 오전 8시부터 시작
    if (year === 2025 && month === 9 && date === 23 && hour >= 8) {
      return true
    }

    // 9월 24일 전체
    if (year === 2025 && month === 9 && date === 24) {
      return true
    }

    // 9월 25일 오전 8시까지
    if (year === 2025 && month === 9 && date === 25 && hour < 8) {
      return true
    }

    return false
  } catch (error) {
    console.error('Error checking paywall time range:', error)
    // 오류 발생 시 안전하게 false 반환
    return false
  }
}

/**
 * 사용자가 paywall을 닫았음을 기록
 */
export const markPaywallDismissed = (): void => {
  try {
    localStorage.setItem('signin_paywall_dismissed_at', Date.now().toString())
  } catch (error) {
    console.warn('Failed to mark paywall as dismissed:', error)
  }
}

/**
 * 사용자가 로그인했음을 기록 (paywall 관련 데이터 정리)
 */
export const markUserLoggedIn = (): void => {
  try {
    localStorage.removeItem('signin_paywall_dismissed_at')
    // 기타 paywall 관련 데이터 정리
  } catch (error) {
    console.warn('Failed to clean up paywall data:', error)
  }
}

/**
 * URL에 utm으로 시작하는 query parameter가 있는지 확인
 */
export const hasUtmParameters = (url?: string): boolean => {
  try {
    const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '')
    if (!targetUrl) return false

    const urlObject = new URL(targetUrl)
    const searchParams = urlObject.searchParams

    // utm으로 시작하는 파라미터가 있는지 확인
    for (const key of searchParams.keys()) {
      if (key.toLowerCase().startsWith('utm')) {
        return true
      }
    }

    return false
  } catch (error) {
    console.warn('Failed to check UTM parameters:', error)
    return false
  }
}

const UTM_USER_PERMANENT = 'utm_user_permanent'
/**
 * UTM 유저임을 영구적으로 localStorage에 저장
 */
export const markUtmUserPermanent = (): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(UTM_USER_PERMANENT, Date.now().toString())
  } catch (error) {
    console.warn('Failed to mark UTM user permanently:', error)
  }
}

/**
 * 사용자가 UTM 유저인지 확인 (영구 저장된 플래그 확인)
 */
export const isUtmUser = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const utmUserFlag = localStorage.getItem(UTM_USER_PERMANENT)
    return !!utmUserFlag
  } catch (error) {
    // localStorage 접근 실패 시 false 반환
    return false
  }
}

/**
 * 테스트 유저인지 확인 (test-user 쿠키가 'true'인 경우)
 */
export const isTestUser = (): boolean => {
  try {
    const testUserCookie = getCookie('test-user')
    return testUserCookie === 'true'
  } catch (error) {
    console.warn('Failed to check test-user cookie:', error)
    return false
  }
}
